/**
 * ImportModal - User Bulk Import Component
 * ---------------------------------------
 * This modal is used for importing USERS in bulk through CSV/XLS files.
 *
 * FEATURES:
 * ✔ Drag-and-drop file upload
 * ✔ Auto-detects column types (Name, Email, Mobile, Reporting To, Designation, Team, etc.)
 * ✔ Allows manual mapping of uploaded columns → system fields
 * ✔ Shows table preview before import
 * ✔ Allows row exclusion using checkboxes
 * ✔ Validates seat availability based on license (no_of_employees)
 * ✔ Sends final cleaned & mapped data to backend using createMultipleUsers()
 *
 * MAPPING LOGIC:
 *  - Uses predefined lists:
 *      nameList, emailList, phoneList, reportingList, profileList, DesignationList, teamList
 *  - Auto-applies default mapping based on column name heuristics
 *  - Prevents duplicate field selections using `selected: true`
 */

import { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import { Checkbox } from "@material-ui/core";
import { IoMdDocument } from "react-icons/io";
import { handleFileUpload } from "../../../Services/contacts";
import { connect, useDispatch } from "react-redux";
import Loading from "../../Loading/Loading";
import { createMultipleUsers } from "../../../Services/users";
import "../../../theme-vars.css";
import { useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import {
  DesignationList,
  emailList,
  nameList,
  phoneList,
  profileList,
  reportingList,
  teamList,
} from "../../../Values/tables";
import { themeColors } from "../../theme";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import StyledCancelButton from "../../Buttons/StyledCancelButton";

type props = {
  open: boolean;
  close: () => void;
  organization_id: string;
  history: any;
  usersList: any[];
  user: any;
  organization: any;
  organizationUsers: any;
  theme: any;
};

const ImportModal: FunctionComponent<props> = ({
  open,
  close,
  organization_id,
  history,
  usersList,
  user,
  organization,
  organizationUsers,
  theme
}) => {

  /** MUI theme for responsive adjustments */
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /** Extracted column headers from file */
  const [columns, setColumns] = useState([]);

  /** Parsed data rows from CSV/XLS file */
  const [data, setData] = useState([]);

  /** Tracks which rows user wants to EXCLUDE from import */
  const [select, setSelected] = useState<any[]>([]);

  /** Loading flag for file upload / processing */
  const [load, setLoad] = useState(false);

  /**
   * Mapping object:
   * {
   *   "Uploaded Column A": "Name",
   *   "Uploaded Column B": "Email ID",
   *   ...
   * }
   */
  const [colName, setColName] = useState<{ [key: string]: string }>({});

  /**
   * Available mapping fields.
   * selected = true → this system field already mapped → disable it.
   */
  const [choice, setChoice] = useState([
    { name: "Name", selected: false },
    { name: "Mobile No.", selected: false },
    { name: "Email ID", selected: false },
    { name: "Reporting To", selected: false },
    { name: "Profile", selected: false },
    { name: "Designation", selected: false },
    { name: "Team", selected: false },
    { name: "Branch", selected: false },
  ]);

  /**
   * Automatically detects column types after upload.
   * Uses heuristic lists: nameList, emailList, reportingList, etc.
   */
  useEffect(() => {
    let mapped: any = {};

    columns.forEach((col: any) => {
      mapped = {
        ...mapped,
        [col.name]:
          nameList.includes(col.name) ? "Name" :
            emailList.includes(col.name) ? "Email ID" :
              phoneList.includes(col.name) ? "Mobile No." :
                reportingList.includes(col.name) ? "Reporting To" :
                  profileList.includes(col.name) ? "Profile" :
                    DesignationList.includes(col.name) ? "Designation" :
                      teamList.includes(col.name) ? "Team" :
                        "",
      };
    });

    setColName(mapped);
  }, [columns]);

  /**
   * Updates mapping availability.
   * Marks system fields as selected if already mapped to prevent duplicates.
   */
  useEffect(() => {
    const columns = Object.values(colName);
    let tempChoices = [...choice];
    tempChoices.forEach((choice, index) => {
      if (columns.includes(choice.name)) {
        tempChoices[index].selected = true;
      } else {
        tempChoices[index].selected = false;
      }
    });

    setChoice(tempChoices);
    // eslint-disable-next-line
  }, [colName]);

  /**
   * Uploads CSV/XLS file and extracts:
   *  - column headers
   *  - row data
   */
  const upload = (file: any) => {
    setLoad(true);
    handleFileUpload(
      file,
      (rows: any) => setData(rows),
      (cols: any) => setColumns(cols),
      (loading: boolean) => setLoad(loading)
    );
  };

  const dispatcher = useDispatch();

  /**
   * Final submission of imported + mapped user data.
   * Performs:
   *  - Row filtering
   *  - Mapping conversion
   *  - License validation (no_of_employees)
   *  - API call: createMultipleUsers()
   */
  const onSubmit = () => {
    const mapping: { [key: string]: string } = {};

    /** Build mapping object excluding blank selections */
    Object.keys(colName).forEach((key) => {
      if (colName[key] !== "") mapping[colName[key]] = key;
    });

    /** Clone rows & remove excluded rows */
    let filteredRows = [...data];
    select.sort((a, b) => b - a); // delete from end
    select.forEach((index) => filteredRows.splice(index, 1));

    /** Count active users to validate license limit */
    const activeUsers = organizationUsers.filter(
      (item: any) => item.status === "ACTIVE"
    );

    /** Send to backend */
    createMultipleUsers(
      filteredRows,
      mapping,
      organization_id,
      dispatcher,
      close,
      `${user.user_first_name} ${user.user_last_name}`,
      (loading: boolean) => setLoad(loading),
      history,
      activeUsers.length,
      Number(organization.no_of_employees),
      organization.country,
      organization.state
    );
  };

  /**
   * Modal style configuration
   */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme ? themeColors.cardBackgroundColor : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "10px 20px",
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={modalStyle}
      ariaHideApp={false}
    >
      {load && <Loading />}

      {/* If file is uploaded → show table preview */}
      {data.length !== 0 ? (
        <>
          <h5
            style={{
              fontFamily: "var(--font-family-main)",
              fontSize: "var(--font-size-large)",
              paddingTop: isMobile ? "unset" : "15px",
            }}
          >
            Import Data for "User Form"
          </h5>

          {/* Table preview */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead className={styles.head} style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}>
                <tr>
                  <th className={styles.th}></th>
                  <th className={styles.th}>Sno.</th>

                  {/* Column Headers + Mapping Dropdown */}
                  {columns.map((col: any, index: number) => (
                    <th key={index} className={styles.th}>
                      <div className={styles.tHeadContainer}>
                        <p>{col.name}</p>
                      </div>

                      {/* Column Mapping */}
                      <div className={styles.drop}>
                        <select
                          className={styles.options}
                          onChange={(val) => {
                            let updated = { ...colName };
                            Object.keys(updated).forEach((key) => {
                              if (key === col.name) {
                                updated[key] =
                                  val.target.value === "Select"
                                    ? ""
                                    : val.target.value;
                              }
                            });
                            setColName(updated);
                          }}
                        >
                          {/* Auto-mapped default */}
                          <option>
                            {nameList.includes(col.name)
                              ? "Name"
                              : emailList.includes(col.name)
                                ? "Email ID"
                                : phoneList.includes(col.name)
                                  ? "Mobile No."
                                  : reportingList.includes(col.name)
                                    ? "Reporting To"
                                    : profileList.includes(col.name)
                                      ? "Profile"
                                      : DesignationList.includes(col.name)
                                        ? "Designation"
                                        : teamList.includes(col.name)
                                          ? "Team"
                                          : "Select"}
                          </option>

                          {/* Mapping options */}
                          {choice.map((opt: any, i: number) => (
                            <option disabled={opt.selected} key={i}>
                              {opt.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </th>
                  ))}

                  <th className={styles.th}>Sno.</th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {data.map((row: any, rowIndex: number) => (
                  <tr
                    key={rowIndex}
                    className={styles.no}
                    style={{
                      backgroundColor: select.includes(rowIndex)
                        ? "#00000030"
                        : "#ffffff",
                      borderBottomColor: "#f3f3f3",
                      borderBottomWidth: "5px",
                    }}
                  >
                    <td>
                      {/* Include/Exclude Row Checkbox */}
                      <Checkbox
                        key={rowIndex}
                        checked={select.includes(rowIndex) ? false : true}
                        onChange={(e) => {

                          if (!e.target.checked) {
                            let data = [...select];
                            data.push(rowIndex);
                            setSelected(data);
                          } else if (e.target.checked) {
                            let data = [...select];
                            let item = select.indexOf(rowIndex);
                            if (item > -1) {
                              data.splice(item, 1);
                            }
                            setSelected(data);
                          }
                        }}
                      />
                    </td>

                    <td className={styles.td}>{rowIndex + 1}</td>

                    {/* Render all row cell values */}
                    {Object.values(row).map((cell: any, i) => (
                      <td className={styles.td} key={i}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Action Buttons */}
          <div className={styles.buttonBox}>
            <StyledCancelButton onClick={close}>Cancel</StyledCancelButton>
            <StyledSubmitButton onClick={onSubmit}>Confirm</StyledSubmitButton>
          </div>
        </>
      ) : (
        /** BEFORE FILE UPLOAD UI */
        <div
          className={styles.child}
          style={{ height: isMobile ? "50vh" : "70vh" }}
        >
          <h5
            style={{
              fontFamily: "var(--font-family-main)",
              fontSize: "var(--font-size-large)",
              paddingTop: isMobile ? "unset" : "15px",
            }}
          >
            Import Data for "User Form"
          </h5>

          {/* Drag and Drop Upload Box */}
          <div
            className={styles.dragBox}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              upload(e.dataTransfer.files[0]);
            }}
          >
            <IoMdDocument size={100} color={"#808080"} />
            <p>Drag Your Files Here</p>
          </div>

          {/* File Selection Buttons */}
          <div className={styles.selectBox}>
            <div style={{ marginRight: 8 }}>
              <StyledCancelButton onClick={close}>
                Cancel
              </StyledCancelButton>
            </div>
            <div>
              <input
                id="file-input"
                className={styles.select}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e: any) => upload(e.target.files[0])}
              />

              <StyledSubmitButton
                onClick={() =>
                  document.getElementById("file-input")?.click()
                }
              >
                Upload File
              </StyledSubmitButton>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: any) => {
  return {
    organization: state.organization,
    organizationUsers: state.organizationUsers.data,
    theme: state.theme.isLightMode,
  };
};

export default connect(mapStateToProps)(ImportModal);
